import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';
type AccentColor = string; // Allow any color string for custom colors

interface ThemeContextType {
  theme: Theme;
  accentColor: AccentColor;
  setTheme: (theme: Theme) => void;
  setAccentColor: (color: AccentColor) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>('light');
  const [accentColor, setAccentColorState] = useState<AccentColor>('#6b46c1');

  useEffect(() => {
    // Load saved theme and accent color from localStorage
    const savedTheme = localStorage.getItem('hubTheme') as Theme | null;
    const savedAccent = localStorage.getItem('hubAccentColor') as AccentColor | null;

    if (savedTheme) {
      setThemeState(savedTheme);
      applyTheme(savedTheme);
    }
    if (savedAccent) {
      setAccentColorState(savedAccent);
      applyAccentColor(savedAccent);
    } else {
      // Apply default purple color on first load
      applyAccentColor('#6b46c1');
    }
  }, []);

  const applyTheme = (newTheme: Theme) => {
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  const applyAccentColor = (color: AccentColor) => {
    document.documentElement.style.setProperty('--accent-color', color);

    // Calculate RGB values for rgba usage
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    document.documentElement.style.setProperty('--accent-color-rgb', `${r}, ${g}, ${b}`);

    // Calculate lighter and darker shades
    const lighter = adjustColor(color, 30);
    const darker = adjustColor(color, -30);
    const lightest = getLightestShade(color);

    document.documentElement.style.setProperty('--accent-color-light', lighter);
    document.documentElement.style.setProperty('--accent-color-dark', darker);
    document.documentElement.style.setProperty('--accent-color-lightest', lightest);
    
    // Force re-render by triggering a style recalculation
    document.body.style.display = 'none';
    void document.body.offsetHeight; // Trigger reflow
    document.body.style.display = '';
  };

  const getLightestShade = (color: string): string => {
    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Mix with white (90% white, 10% color)
    const nr = Math.round(r * 0.1 + 255 * 0.9);
    const ng = Math.round(g * 0.1 + 255 * 0.9);
    const nb = Math.round(b * 0.1 + 255 * 0.9);
    
    return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`;
  };

  const adjustColor = (color: string, amount: number): string => {
    const num = parseInt(color.replace('#', ''), 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + amount));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
    const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    applyTheme(newTheme);
    localStorage.setItem('hubTheme', newTheme);
  };

  const setAccentColor = (color: AccentColor) => {
    setAccentColorState(color);
    applyAccentColor(color);
    localStorage.setItem('hubAccentColor', color);
  };

  return (
    <ThemeContext.Provider value={{ theme, accentColor, setTheme, setAccentColor }}>
      {children}
    </ThemeContext.Provider>
  );
};
